// File: src/pages/PostDetailPage.js

import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';

import { useProfile } from '../context/ProfileContext';
import useSongActions from '../hooks/useSongActions';
import ProfileLink from '../components/profile/ProfileLink';
import EmbeddedSongLinkCard from '../components/press/EmbeddedSongLinkCard';

// Placeholder for the upcoming EmbeddedSongPlayer component
const EmbeddedSongPlayerPlaceholder = ({ songId, songBoxTitle }) => (
    <div className="mb-6 p-4 rounded-2xl border border-dashed border-[#ff00cc]/50 bg-[#ff00cc]/5 text-center">
        <p className="text-xs font-bold text-slate-900 dark:text-white">
            {songBoxTitle || "Stream Track"}
        </p>
        <p className="text-[10px] text-[#ff00cc] mt-1 font-semibold">
            [Embedded Song Player Component Placeholder — Song ID: {songId || 'N/A'}]
        </p>
    </div>
);

// Utility helper to safely extract domain names from URLs
const getDomainName = (url) => {
    try {
        if (!url) return '';
        const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
        return parsed.hostname.replace('www.', '');
    } catch {
        return url;
    }
};

const PostDetailPage = ({ post: postProp, artistProfile: artistProfileProp }) => {
    const { postId } = useParams();

    // Local state for async Firestore document fetching
    const [internalPost, setInternalPost] = useState(null);
    const [internalArtistProfile, setInternalArtistProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const { isDarkMode } = useProfile();

    // Fetch Post and Artist documents if not passed directly via props
    useEffect(() => {
        const fetchPostData = async () => {
            try {
                const db = getFirestore();
                const postRef = doc(db, 'artistPosts', postId);
                const postSnap = await getDoc(postRef);

                if (postSnap.exists()) {
                    const postData = { id: postSnap.id, ...postSnap.data() };
                    setInternalPost(postData);

                    if (postData.artistId) {
                        const artistRef = doc(db, 'artist_accounts', postData.artistId);
                        const artistSnap = await getDoc(artistRef);
                        if (artistSnap.exists()) {
                            setInternalArtistProfile({ id: artistSnap.id, ...artistSnap.data() });
                        }
                    }
                } else {
                    console.error("Post document not found!");
                }
            } catch (err) {
                console.error("Error fetching post record:", err);
            } finally {
                setIsLoading(false);
            }
        };

        if (!postProp && postId) {
            fetchPostData();
        } else {
            setIsLoading(false);
        }
    }, [postId, postProp]);

    const post = postProp || internalPost;
    const artistProfile = artistProfileProp || internalArtistProfile;

    // Derived properties
    const { title, body } = useMemo(() => {
        if (!post) return { title: 'Loading...', body: '' };

        // 1. Primary: Prefer artist's exact authored text
        if (post.title || post.text) {
            return {
                title: post.title || post.atomifiedTitle || 'Untitled Announcement',
                body: post.text || post.body || post.atomifiedText || ''
            };
        }

        // 2. Secondary: Fall back to AI atomified text
        if (post.atomifiedTitle || post.atomifiedText) {
            return {
                title: post.atomifiedTitle || 'Untitled Announcement',
                body: post.atomifiedText || ''
            };
        }

        // 3. Legacy: Parse line-separated officialText
        const oldText = post.officialText || '';
        const derivedTitle = oldText.split('\n')[0] || "Untitled Announcement";
        const derivedBody = oldText.substring(derivedTitle.length).trim();
        return { title: derivedTitle, body: derivedBody };
    }, [post]);

    // Subscription Action Hook
    const { isSubscribed, toggleSubscription } = useSongActions({
        songId: post?.songId,
        artistId: post?.artistId
    });

    const domain = getDomainName(post?.externalUrl);
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

    // Native Social Share Handler
    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: title,
                    text: `Check out this post by ${artistProfile?.artistName || 'the artist'}!`,
                    url: currentUrl,
                });
            } catch (err) {
                console.log("Share prompt dismissed:", err);
            }
        } else {
            await navigator.clipboard.writeText(currentUrl);
            toast.success("Link copied to clipboard!");
        }
    };

    // Subscription Action Handler
    const handleSubscribe = (e) => {
        e.stopPropagation();
        if (!post?.artistId) return;
        toggleSubscription(post.artistId, artistProfile?.artistName || 'Artist', 'post_detail_page');
    };

    // Loading State
    if (isLoading) {
        return (
            <div className="max-w-2xl mx-auto p-6 min-h-screen flex items-center justify-center">
                <p className={`text-center text-xs font-black uppercase tracking-widest animate-pulse ${
                    isDarkMode ? 'text-zinc-600' : 'text-slate-400'
                }`}>
                    Querying Music Blog Record...
                </p>
            </div>
        );
    }

    // Missing Post Fallback
    if (!post || !artistProfile) {
        return (
            <div className="max-w-2xl mx-auto p-6 min-h-screen flex items-center justify-center">
                <p className={`text-center text-sm font-bold ${
                    isDarkMode ? 'text-zinc-500' : 'text-slate-400'
                }`}>
                    Requested Announcement Node Not Found.
                </p>
            </div>
        );
    }

    return (
        <>
            {/* Dynamic Open Graph Meta Tags for Social Previews */}
            <Helmet>
                <title>{`${title} | ${artistProfile.artistName || 'Music Blog'}`}</title>
                <meta name="description" content={body.substring(0, 160)} />

                {/* Open Graph / Facebook / WhatsApp */}
                <meta property="og:type" content="article" />
                <meta property="og:url" content={currentUrl} />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={body.substring(0, 160)} />
                {post.mediaUrl && <meta property="og:image" content={post.mediaUrl} />}

                {/* Twitter Meta Tags */}
                <meta name="twitter:card" content={post.mediaUrl ? "summary_large_image" : "summary"} />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={body.substring(0, 160)} />
                {post.mediaUrl && <meta name="twitter:image" content={post.mediaUrl} />}
            </Helmet>

            {/* Main Article Canvas */}
            <div className={`max-w-2xl mx-auto p-4 sm:p-6 min-h-screen transition-colors duration-300 ${
                isDarkMode ? 'bg-zinc-950 text-white' : 'bg-white text-slate-900'
            }`}>
                <div className={`border rounded-3xl p-5 sm:p-7 shadow-md transition-all duration-300 ${
                    isDarkMode 
                        ? "bg-zinc-950 border-zinc-800 shadow-black/40" 
                        : "bg-white border-slate-200 shadow-slate-100"
                }`}>
                    
                    

                    {/* 1. MEDIA IMAGE (Strictly images, video completely omitted) */}
                    {post.mediaUrl && (
                        <div className="mb-6 overflow-hidden rounded-2xl border border-zinc-800/20 shadow-sm">
                            <img 
                                src={post.mediaUrl} 
                                alt={title} 
                                className="w-full max-h-[420px] object-cover"
                            />
                        </div>
                    )}

                    {/* 2. TITLE */}
                    <h1 className="text-2xl sm:text-3xl font-black tracking-tight mb-2 uppercase italic leading-tight text-slate-900 dark:text-white">
                        {title}
                    </h1>

                    {/* Timestamp */}
                    <p className={`text-[10px] font-black uppercase tracking-widest mb-4 ${
                        isDarkMode ? 'text-zinc-500' : 'text-gray-400'
                    }`}>
                        Published {post.createdAt?.seconds 
                            ? new Date(post.createdAt.seconds * 1000).toLocaleDateString() 
                            : 'Recently'}
                    </p>

                    {/* 3. BODY TEXT */}
                    <p className={`whitespace-pre-wrap text-sm font-semibold leading-relaxed mb-6 ${
                        isDarkMode ? 'text-zinc-300' : 'text-gray-700'
                    }`}>
                        {body}
                    </p>

                    {/* 4. EMBEDDED SONG PLAYER (Placeholder) */}
                    {post.songId && (
                        <EmbeddedSongLinkCard 
                            songId={post.songId} 
                            songBoxTitle={post.songBoxTitle}
                            songData={post.songData} // 🌟 Passed directly from post document
                        />
                    )}

                    {/* 5. EXTERNAL URL LINK CARD */}
                    {post.externalUrl && post.externalUrl.trim() !== '' && (
                        <div className="mb-6 space-y-1.5">
                            {post.urlBoxTitle && post.urlBoxTitle.trim() !== '' && (
                                <p className="text-xs font-bold text-slate-900 dark:text-white">
                                    {post.urlBoxTitle}
                                </p>
                            )}

                            <a
                                href={post.externalUrl.startsWith('http') ? post.externalUrl : `https://${post.externalUrl}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center justify-between p-3.5 rounded-2xl border transition-all group cursor-pointer ${
                                    isDarkMode 
                                        ? "bg-zinc-900/60 border-zinc-800 hover:border-[#ff00cc]/50 hover:bg-zinc-900" 
                                        : "bg-slate-50 border-slate-200 hover:border-[#ff00cc]/50 hover:bg-slate-100"
                                }`}
                            >
                                <div className="flex items-center space-x-3 overflow-hidden pr-2">
                                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 border ${
                                        isDarkMode ? "bg-zinc-950 border-zinc-800 text-[#ff00cc]" : "bg-white border-slate-200 text-[#ff00cc]"
                                    }`}>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.007 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.997 0 0121 12c0 .778-.099 1.533-.284 2.253m-17.432 0A8.959 8.959 0 013 12c0-.778.099-1.533.284-2.253" />
                                        </svg>
                                    </div>
                                    <div className="overflow-hidden">
                                        <p className={`text-xs font-bold truncate group-hover:text-[#ff00cc] transition-colors ${
                                            isDarkMode ? "text-white" : "text-slate-900"
                                        }`}>
                                            {post.externalUrl}
                                        </p>
                                        <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 truncate">
                                            {domain || 'External Link'}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex-shrink-0 text-slate-400 group-hover:text-[#ff00cc] transition-colors">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                    </svg>
                                </div>
                            </a>
                        </div>
                    )}

                    {/* 6. ARTIST PROFILE ROW WITH ProfileLink */}
                    <div className="flex items-center pt-4 border-t border-slate-100 dark:border-zinc-800/80 mb-6">
                        <ProfileLink 
                            profileId={post.artistId}
                            photoURL={artistProfile.photoURL}
                            altText={artistProfile.artistName || 'Artist'}
                            size="w-10 h-10"
                            source="post_detail_page"
                            isArtist={true}
                        />
                        <div className="ml-3">
                            <p className="font-black text-xs tracking-tight uppercase italic text-slate-900 dark:text-white">
                                {artistProfile.artistName || 'Artist Name'}
                            </p>
                            
                        </div>
                    </div>

                    {/* 7. ACTION BUTTON FOOTER (Subscribe & Share ONLY) */}
                    <div className="grid grid-cols-2 gap-3 pt-2">
                        <button 
                            type="button"
                            onClick={handleSubscribe}
                            className={`py-2.5 px-4 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all shadow-sm active:scale-95 ${
                                isSubscribed
                                    ? 'bg-[#ff00cc]/10 text-[#ff00cc] border border-[#ff00cc]/40'
                                    : isDarkMode 
                                        ? 'bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-800' 
                                        : 'bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-200'
                            }`}
                        >
                            {isSubscribed ? 'Subscribed' : 'Subscribe'}
                        </button>

                        <button 
                            type="button"
                            onClick={handleShare}
                            className="py-2.5 px-4 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all shadow-md bg-[#ff00cc] text-white hover:brightness-110 active:scale-95"
                        >
                            Share
                        </button>
                    </div>

                </div>
            </div>
        </>
    );
};

export default PostDetailPage;